import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MojoPrivilegesService } from 'mojo-privileges';
import { of } from 'rxjs';
import { catchError, filter, first, map, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

import { LoadAggregatedJobsData } from '@collections/aggregations/aggregations.actions';
import { getJobsSelectedId, JobsActionTypes } from '@collections/jobs';
import { getJobPageCandidatesState, jobCandidatesLoad } from '@modules/job-page/store';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import { getFeedbackMessageForHM, getRandomSuccessMessage } from '@shared/feedback/feedback.config';
import { Feedback, FeedbackType, ThumbValue } from '@shared/models';
import { Pagination } from '@shared/pagination';
import { FeedbackService } from '@shared/services/feedback.service';
import { isTruthy } from '@shared/utils';

import { CollectionsConfig } from '../collections.abstract.config';
import * as FeedbackActions from './feedback.actions';

@Injectable()
export class FeedbackEffects {
  loadForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.SelectSuccess),
      pluck('payload'),
      switchMap((jobId: number) =>
        this.service.getForJob(jobId).pipe(
          map((feedbackArray: Feedback[]) =>
            FeedbackActions.feedbackLoadSuccess({ feedbackArray }),
          ),
          catchError((error) => of(FeedbackActions.feedbackLoadFail(error))),
        ),
      ),
    ),
  );

  loadFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackLoadFail),
      map(() => new DangerAlert('There was a problem loading feedback')),
    ),
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackCreate),
      switchMap(({ feedback }) =>
        this.service.post({ body: feedback }).pipe(
          map((response: Feedback) =>
            FeedbackActions.feedbackCreateSuccess({
              feedback: response,
            }),
          ),
          catchError((error) => of(FeedbackActions.feedbackCreateFail({ error }))),
        ),
      ),
    ),
  );

  createMany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackCreateMany),
      switchMap(({ feedbackArray }) =>
        this.service.post({ url: 'many', body: feedbackArray }).pipe(
          map((response: Feedback[]) =>
            FeedbackActions.feedbackCreateManySuccess({
              feedbackArray: response,
            }),
          ),
          catchError((error) => of(FeedbackActions.feedbackCreateFail({ error }))),
        ),
      ),
    ),
  );

  createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackCreateSuccess, FeedbackActions.feedbackCreateManySuccess),
      withLatestFrom(
        this.privilegesService.hasPrivilege(this.collectionsConfig.privileges.hiringManager),
      ),
      map(([feedback, isHiringManager]) => {
        let message;
        if (isHiringManager && 'feedbackArray' in feedback) {
          message = getFeedbackMessageForHM(feedback.feedbackArray[0].type);
        } else {
          this.store.dispatch(
            LoadAggregatedJobsData({ jobsIds: [], updateSingleAggregatedJobData: true }),
          );
          message = getRandomSuccessMessage();
        }
        return new SuccessAlert(message);
      }),
    ),
  );

  createSuccessReloadMatches = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackCreateManySuccess),
      pluck('feedbackArray'),
      filter(
        (feedbackArray: Feedback[]) =>
          feedbackArray.find((feedback) => feedback.type === FeedbackType.THUMB)?.value ===
          ThumbValue.DOWN,
      ),
      switchMap(() => this.store.select(getJobsSelectedId).pipe(first(), filter(isTruthy))),
      switchMap(() => this.store.select(getJobPageCandidatesState).pipe(first())),
      switchMap(({ number }: Pagination) => [
        jobCandidatesLoad({
          params: { offset: number },
        }),
      ]),
    ),
  );

  createFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeedbackActions.feedbackCreateFail),
      map(() => new DangerAlert('There was a problem creating feedback')),
    ),
  );

  constructor(
    private store: Store<{}>,
    private actions$: Actions,
    private service: FeedbackService,
    private privilegesService: MojoPrivilegesService,
    private collectionsConfig: CollectionsConfig,
  ) {}
}
